/**
 * AI Storybook — preparing a family photo on the device, before it is sent
 * anywhere.
 *
 * Photos of the child and of the family members in the story (every relation
 * but a friend, config.photoRelations) may be added; the parent marks who each
 * face is and the server keeps only those face crops (docs/storybook-identity-
 * lock.md §2.2). A photo is decoded with the camera's rotation applied,
 * downsized to config.limits.photoLongEdge (2048px) on its long edge and
 * re-encoded as JPEG (quality 0.88) through a canvas. Re-encoding drops every
 * bit of metadata, EXIF and GPS position included, and bakes the rotation in,
 * so the face boxes the parent marks are in the pixels the server reads.
 *
 * Sizes are compared the way the server measures them: DECODED bytes
 * (ceil(base64 length * 3 / 4)), not the length of the base64 text.
 */

export const STORYBOOK_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const STORYBOOK_PHOTO_ACCEPT = STORYBOOK_PHOTO_TYPES.join(',');
/** Per photo, decoded bytes, used when GET /config does not say (the backend's own default). */
export const STORYBOOK_DEFAULT_MAX_PHOTO_BYTES = 2 * 1024 * 1024;
/** Long edge (px) used when GET /config does not say. */
export const STORYBOOK_DEFAULT_PHOTO_LONG_EDGE = 2048;
export const STORYBOOK_PHOTO_QUALITY = 0.88;
/**
 * A photo whose JPEG decodes to more than this is encoded again, smaller: a
 * margin under the backend's 2 MiB per-photo cap, so a photo never fails on
 * the server for being a few bytes over.
 */
export const STORYBOOK_PHOTO_TARGET_BYTES = Math.round(1.9 * 1024 * 1024);
/** The backend refuses photos whose short side is under 200px (checked on what it receives). */
export const STORYBOOK_PHOTO_MIN_EDGE = 200;

/** Qualities tried at full size, then at every smaller size. */
const QUALITY_STEPS = [STORYBOOK_PHOTO_QUALITY, 0.8, 0.72] as const;
const SMALLER_QUALITY = 0.8;
/** Each smaller try keeps this share of the long edge (at most MAX_SHRINKS times). */
const SHRINK = 0.8;
const MAX_SHRINKS = 4;

export type StorybookPhotoErrorCode = 'heic' | 'unsupported' | 'unreadable' | 'too_large' | 'too_small';

export class StorybookPhotoError extends Error {
  readonly code: StorybookPhotoErrorCode;

  constructor(code: StorybookPhotoErrorCode) {
    super(code);
    this.name = 'StorybookPhotoError';
    this.code = code;
  }
}

export interface StorybookPhotoOptions {
  /**
   * Byte budget for this photo (decoded). The builder passes the per-photo cap
   * or what is left of the request's total, whichever is smaller; the result
   * never exceeds STORYBOOK_PHOTO_TARGET_BYTES either way.
   */
  maxBytes?: number;
  /** Long edge in px (config.limits.photoLongEdge). */
  longEdge?: number;
}

export interface PreparedStorybookPhoto {
  /** data:image/jpeg;base64,… exactly as it is sent. */
  dataUrl: string;
  /** Pixel size of that JPEG: face boxes are normalized to it. */
  width: number;
  height: number;
  /** Decoded bytes (what the server counts). */
  bytes: number;
}

function isHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

function decodeWithImageElement(file: File): Promise<Decoded> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () =>
      resolve({
        source: img,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        release: () => URL.revokeObjectURL(url),
      });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode_failed'));
    };
    img.src = url;
  });
}

async function decode(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      // Honour the camera's rotation flag before it is thrown away. (An <img>
      // applies it too: CSS image-orientation defaults to from-image.)
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() };
    } catch {
      /* some browsers reject the options bag; the <img> path still works */
    }
  }
  return decodeWithImageElement(file);
}

/** Length of the base64 payload of a data URL (what the request carries). */
export function dataUrlPayloadBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.length - comma - 1 : dataUrl.length;
}

/** Bytes the data URL decodes to, the way the server counts a photo: ceil(base64 length * 3 / 4). */
export function dataUrlDecodedBytes(dataUrl: string): number {
  return Math.ceil((dataUrlPayloadBytes(dataUrl) * 3) / 4);
}

function encode(canvas: HTMLCanvasElement, quality: number): string {
  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  } catch {
    throw new StorybookPhotoError('unreadable');
  }
  if (!dataUrl || !dataUrl.startsWith('data:image/jpeg')) throw new StorybookPhotoError('unreadable');
  return dataUrl;
}

export async function prepareStorybookPhoto(file: File, options: StorybookPhotoOptions = {}): Promise<PreparedStorybookPhoto> {
  if (isHeic(file)) throw new StorybookPhotoError('heic');
  const type = (file.type || '').toLowerCase();
  if (!(STORYBOOK_PHOTO_TYPES as readonly string[]).includes(type)) throw new StorybookPhotoError('unsupported');

  const longEdge = options.longEdge && options.longEdge > 0 ? options.longEdge : STORYBOOK_DEFAULT_PHOTO_LONG_EDGE;
  const budget = Math.min(
    STORYBOOK_PHOTO_TARGET_BYTES,
    options.maxBytes && options.maxBytes > 0 ? options.maxBytes : STORYBOOK_DEFAULT_MAX_PHOTO_BYTES,
  );

  let decoded: Decoded;
  try {
    decoded = await decode(file);
  } catch {
    throw new StorybookPhotoError('unreadable');
  }

  try {
    const { width, height } = decoded;
    if (!width || !height) throw new StorybookPhotoError('unreadable');

    const canvas = document.createElement('canvas');
    let scale = Math.min(1, longEdge / Math.max(width, height));
    for (let attempt = 0; attempt <= MAX_SHRINKS; attempt++) {
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      // The server checks the short side of what it RECEIVES, so the check is
      // on the resized picture: a very wide panorama can pass on the original
      // and still end up under 200px once its long edge is cut down.
      if (Math.min(w, h) < STORYBOOK_PHOTO_MIN_EDGE) {
        throw new StorybookPhotoError(attempt === 0 ? 'too_small' : 'too_large');
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new StorybookPhotoError('unreadable');
      // JPEG has no transparency: paint see-through PNG areas white, not black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(decoded.source, 0, 0, w, h);

      for (const quality of attempt === 0 ? QUALITY_STEPS : [SMALLER_QUALITY]) {
        const dataUrl = encode(canvas, quality);
        const bytes = dataUrlDecodedBytes(dataUrl);
        if (bytes <= budget) return { dataUrl, width: w, height: h, bytes };
      }
      scale *= SHRINK;
    }
    throw new StorybookPhotoError('too_large');
  } finally {
    decoded.release();
  }
}
